import { useUserInfo } from "@/api/user";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { formatInteger } from "@/utils/currency";
import { useMemo } from "react";

interface TokenMeterProps {
  size?: number;
  stroke?: number;
}

export function TokenMeter({ size = 16, stroke = 2.25 }: TokenMeterProps) {
  const { data: userInfo } = useUserInfo();
  const openSettings = useOpenSettings();

  const { pct, used, total } = useMemo(() => {
    const usage = userInfo?.usage;
    const subscription = userInfo?.subscription;
    const usedCredits = usage?.current_period_user_credit_count ?? 0;
    const included = subscription?.included_credits_per_seat ?? 0;
    const totalCredits = included > 0 ? included : 1;
    const pctValue = Math.min(
      100,
      Math.round((usedCredits / totalCredits) * 100),
    );
    return { pct: pctValue, used: usedCredits, total: totalCredits };
  }, [userInfo?.usage, userInfo?.subscription]);

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;

  const ringColor =
    pct >= 90
      ? "var(--lc-blocked)"
      : pct >= 75
        ? "var(--lc-warning)"
        : "var(--lc-active)";

  if (!userInfo?.subscription || !userInfo?.usage) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`AI credits — ${pct}% used`}
          onClick={() => openSettings(ROUTES.SETTINGS.USAGE)}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md outline-none transition-colors hover:bg-n-05 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
        >
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="-rotate-90"
            aria-hidden
          >
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke="var(--n-10)"
              strokeWidth={stroke}
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={ringColor}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${dash} ${c}`}
              className="transition-[stroke-dasharray,stroke] duration-300 ease-out"
            />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="end" className="flex flex-col gap-0.5">
        <span className="font-medium">{pct}% used</span>
        <span className="font-mono text-2xs text-n-30">
          {formatInteger(used)} / {formatInteger(total)} credits
        </span>
      </TooltipContent>
    </Tooltip>
  );
}
